import type { SubEntryType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ModalContentForm,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type { FormEventHandler } from "react"
import { useCallback } from "react"

import { useEntryModalPrivateContext } from "./EntryModalPrivateContext"
import { useEntryModalPublicContext } from "./EntryModalPublicContext"
import { EntrySummary } from "./EntrySummary"
import { SubEntryForm } from "./SubEntryForm"
import { SubEntryTypeSelectors } from "./SubEntryTypeSelectors"
import { useOverlayLoader } from "src/contexts"
import { SaveEntryErrorToast } from "src/toasts"
import type {
  ReadOnlyUserEntry,
  EditableSubEntry,
  EditableUserEntry,
} from "src/types"

interface Props {
  currentUserEntry: EditableUserEntry;
  currentSubEntry: EditableSubEntry | undefined;
  editSubEntry: (e: EditableSubEntry | undefined) => void;
  editUserEntry: (e: EditableUserEntry) => void;
  resetSubEntry: (e: EditableSubEntry | undefined) => void;
}

export function EntryModalContent(props: Props) {
  const {
    currentSubEntry,
    currentUserEntry,
    editSubEntry,
    resetSubEntry,
    editUserEntry,
  } = props

  const {
    saveEntry,
    formId,
  } = useEntryModalPrivateContext()

  const { closeEntryModal } = useEntryModalPublicContext()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          await saveEntry()
          closeEntryModal()
          // TODO show success toast ?
        } catch {
          showToast(
            "save-entry-error-toast",
            SaveEntryErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      showToast,
      saveEntry,
      closeEntryModal,
      setIsOverlayLoaderVisible,
    ],
  )

  return (
    <ModalContentForm
      onSubmit={onSubmit}
      id={formId}
    >
      {
        !currentSubEntry ? (
          <EntrySummary
            currentUserEntry={currentUserEntry as ReadOnlyUserEntry}
            setCurrentUserEntry={editUserEntry}
          />
        ) : currentSubEntry.subentry_type === undefined ? (
          <SubEntryTypeSelectors
            createSubEntry={resetSubEntry}
            currentUserEntry={currentUserEntry}
            currentSubEntry={currentSubEntry as EditableSubEntry<undefined>}
          />
        ) : (
          <SubEntryForm
            currentSubEntry={currentSubEntry as EditableSubEntry<SubEntryType>}
            setCurrentSubEntry={editSubEntry}
            currentUserEntry={currentUserEntry}
            setCurrentUserEntry={editUserEntry}
          />
        )
      }
    </ModalContentForm>
  )
}
